import { DragDropProvider } from "@dnd-kit/react";
import { isSortable, useSortable } from "@dnd-kit/react/sortable";
import { useRef, useState, type ReactNode } from "react";
import { cx, space, type SpaceToken } from "./tokens.ts";

export type SortableListProps<T extends { id: string }> = {
  items: readonly T[];
  renderItem: (item: T) => ReactNode;
  /** An item was dropped at `index` (its position after the drop, in the list without it moved). */
  onMove: (id: string, index: number) => void;
  direction?: "horizontal" | "vertical";
  gap?: SpaceToken;
  /** Horizontal lists wrap onto more lines (a bookmarks bar). */
  wrap?: boolean;
  /** No dragging (read-only). */
  disabled?: boolean;
  /** Shown after the items, not draggable (e.g. an "Add" button). */
  trailing?: ReactNode;
  /**
   * Items that take other items dropped onto them (bookmark folders): releasing over the middle of such an item calls
   * `onDropInto` (the item is outlined meanwhile); its edges place the dragged item before / after it.
   * `canDropInto(sourceId, targetId)` says which pairs.
   */
  canDropInto?: (sourceId: string, targetId: string) => boolean;
  onDropInto?: (sourceId: string, targetId: string) => void;
  accessibilityLabel?: string;
  testID?: string;
};

type Point = { x: number; y: number };
type Rect = { left: number; top: number; width: number; height: number };
type Op = { source: { id: string | number } | null; target: { id: string | number } | null; position: { current: Point } };

/**
 * Where a release at `point` over an item that takes drops lands: its middle half goes into it, the outer quarters
 * before / after it (along the list's direction).
 */
export function dropZoneAt(rect: Rect, point: Point, direction: "horizontal" | "vertical" = "horizontal"): "before" | "into" | "after" {
  const f = direction === "horizontal" ? (point.x - rect.left) / Math.max(1, rect.width) : (point.y - rect.top) / Math.max(1, rect.height);
  return f < 0.25 ? "before" : f > 0.75 ? "after" : "into";
}

/** The `onMove` index for dropping `id` before / after `targetId` (positions in the list without `id`). */
export function indexBeside(ids: readonly string[], id: string, targetId: string, side: "before" | "after"): number {
  const rest = ids.filter((x) => x !== id);
  const at = rest.indexOf(targetId);
  return at < 0 ? rest.length : side === "before" ? at : at + 1;
}

function Item({ id, index, group, disabled, over, children }: { id: string; index: number; group: string; disabled: boolean; over: boolean; children: ReactNode }) {
  const { ref, isDragging } = useSortable({ id, index, group, disabled });
  return (
    <div ref={ref} data-sortable-id={id} className={cx("b-sortable-item", isDragging && "b-sortable-item-dragging", over && "b-sortable-item-over")}>
      {children}
    </div>
  );
}

/**
 * A short list whose items are reordered by dragging (dnd-kit sortable, like the tab strip and Board): a row of chips
 * or a vertical list. The live drag preview is dnd-kit's; the result is reported once through `onMove` and the
 * caller's new `items` take over. With `onDropInto`, some items (folders) also take the dragged item: dnd-kit's live
 * reordering pauses over them and the release point decides (middle: into it; edges: beside it).
 */
export function SortableList<T extends { id: string }>({ items, renderItem, onMove, direction = "horizontal", gap = "1", wrap = false, disabled = false, trailing, canDropInto, onDropInto, accessibilityLabel, testID }: SortableListProps<T>) {
  const group = testID ?? "sortable";
  const list = useRef<HTMLDivElement>(null);
  const [overId, setOverId] = useState<string | undefined>(undefined);
  const takes = (sourceId: string, targetId: string) => onDropInto !== undefined && sourceId !== targetId && items.some((i) => i.id === targetId) && (canDropInto?.(sourceId, targetId) ?? true);
  // The drop-into item under the pointer and which part of it, while dragging.
  const zone = (op: Op) => {
    if (op.source === null || op.target === null) return undefined;
    const target = String(op.target.id);
    if (!takes(String(op.source.id), target)) return undefined;
    const el = list.current?.querySelector(`[data-sortable-id="${CSS.escape(target)}"]`);
    return el === null || el === undefined ? undefined : { target, at: dropZoneAt(el.getBoundingClientRect(), op.position.current, direction) };
  };
  const hover = (op: Op) => {
    const z = zone(op);
    const next = z?.at === "into" ? z.target : undefined;
    setOverId((cur) => (cur === next ? cur : next));
    return z;
  };
  return (
    <DragDropProvider
      onDragOver={(event) => {
        // Over a folder: no live reordering; the release point decides (into it, or before / after it).
        if (hover(event.operation) !== undefined) event.preventDefault();
      }}
      onDragMove={(event) => {
        if (onDropInto !== undefined) hover(event.operation);
      }}
      onDragEnd={(event) => {
        const z = event.canceled ? undefined : zone(event.operation);
        setOverId(undefined);
        const { source } = event.operation;
        if (event.canceled || source === null) return;
        const id = String(source.id);
        if (z !== undefined) {
          if (z.at === "into") onDropInto?.(id, z.target);
          else {
            const ids = items.map((i) => i.id);
            const index = indexBeside(ids, id, z.target, z.at);
            if (index !== ids.indexOf(id)) onMove(id, index);
          }
          return;
        }
        if (isSortable(source) && source.index !== source.initialIndex) onMove(id, source.index);
      }}
    >
      <div
        ref={list}
        className={cx("b-sortable", direction === "vertical" ? "b-sortable-vertical" : "b-sortable-horizontal", wrap && "b-sortable-wrap")}
        style={{ gap: space(gap) }}
        role="list"
        aria-label={accessibilityLabel}
        data-testid={testID}
      >
        {items.map((item, i) => (
          <Item key={item.id} id={item.id} index={i} group={group} disabled={disabled} over={overId === item.id}>
            {renderItem(item)}
          </Item>
        ))}
        {trailing}
      </div>
    </DragDropProvider>
  );
}
